/**
 * Persian words for whole toman amounts (material-standard §3.3, high-stakes supporting text).
 */

import { toPersianDigits } from './digits.js';
import { TOMAN_LABEL } from './money.js';

const ONES = ['', 'یک', 'دو', 'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه'] as const;
const TENS = ['', 'ده', 'بیست', 'سی', 'چهل', 'پنجاه', 'شصت', 'هفتاد', 'هشتاد', 'نود'] as const;
const TEENS = [
  'ده',
  'یازده',
  'دوازده',
  'سیزده',
  'چهارده',
  'پانزده',
  'شانزده',
  'هفده',
  'هجده',
  'نوزده',
] as const;
const HUNDREDS = [
  '',
  'صد',
  'دویست',
  'سیصد',
  'چهارصد',
  'پانصد',
  'ششصد',
  'هفتصد',
  'هشتصد',
  'نهصد',
] as const;

const SCALE_LABELS = ['', 'هزار', 'میلیون', 'میلیارد', 'تریلیون'] as const;

function threeDigitsToWords(value: number): string {
  if (value === 0) {
    return '';
  }
  const hundred = Math.floor(value / 100);
  const remainder = value % 100;
  const parts: string[] = [];
  if (hundred > 0) {
    parts.push(HUNDREDS[hundred] ?? '');
  }
  if (remainder > 0) {
    if (remainder < 10) {
      parts.push(ONES[remainder] ?? '');
    } else if (remainder < 20) {
      parts.push(TEENS[remainder - 10] ?? '');
    } else {
      const tens = Math.floor(remainder / 10);
      const ones = remainder % 10;
      parts.push(TENS[tens] ?? '');
      if (ones > 0) {
        parts.push(ONES[ones] ?? '');
      }
    }
  }
  return parts.filter(Boolean).join(' و ');
}

function integerToWords(value: number): string {
  if (value === 0) {
    return 'صفر';
  }
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError('tomanToWords expects a non-negative safe integer.');
  }

  const chunks: string[] = [];
  let remaining = value;
  let scale = 0;
  while (remaining > 0) {
    const chunk = remaining % 1000;
    if (chunk > 0) {
      const words = threeDigitsToWords(chunk);
      const label = SCALE_LABELS[scale] ?? '';
      chunks.unshift(label === '' ? words : `${words} ${label}`);
    }
    remaining = Math.floor(remaining / 1000);
    scale += 1;
  }
  return chunks.join(' و ');
}

/**
 * Whole toman amount in words with Persian digits in the numeral when `withDigits` is true in options.
 * Example: `1_000_000` → `یک میلیون تومان`.
 */
export function tomanToWords(
  toman: number,
  options: { readonly withPersianDigits?: boolean } = {},
): string {
  const words = integerToWords(toman);
  const prefix =
    options.withPersianDigits === true ? `${toPersianDigits(toman)} (${words})` : words;
  return `${prefix} ${TOMAN_LABEL}`;
}
